<template>
  <div>
    <b-card>
      <!-- Header -->
      <b-row class="mb-3">
        <b-col>
          <h3 class="mb-0">Payment Management</h3>
          <p class="text-muted">View and manage payment transactions</p>
        </b-col>
        <b-col class="text-right">
          <b-button variant="primary" @click="refreshPayments">
            <feather type="refresh-cw" class="feather-sm"></feather>
            Refresh
          </b-button>
        </b-col>
      </b-row>

      <!-- Filters -->
      <b-row class="mb-3">
        <b-col md="3">
          <b-form-select v-model="filter.status" @change="filterPayments">
            <b-form-select-option :value="null">All Status</b-form-select-option>
            <b-form-select-option value="completed">Completed</b-form-select-option>
            <b-form-select-option value="pending">Pending</b-form-select-option>
            <b-form-select-option value="failed">Failed</b-form-select-option>
          </b-form-select>
        </b-col>
        <b-col md="6">
          <b-form-input
            v-model="filter.search"
            placeholder="Search by student name or course"
            @input="searchPayments"
          ></b-form-input>
        </b-col>
      </b-row>

      <!-- Stats Cards -->
      <b-row class="mb-4">
        <b-col md="3">
          <b-card bg-variant="success" text-variant="white">
            <h3 class="mb-0">PKR {{ formatMoney(stats.totalRevenue) }}</h3>
            <p class="mb-0">Total Revenue</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="primary" text-variant="white">
            <h3 class="mb-0">{{ stats.totalPayments || 0 }}</h3>
            <p class="mb-0">Total Payments</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="warning" text-variant="white">
            <h3 class="mb-0">{{ stats.pendingPayments || 0 }}</h3>
            <p class="mb-0">Pending</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="info" text-variant="white">
            <h3 class="mb-0">PKR {{ formatMoney(stats.monthlyRevenue) }}</h3>
            <p class="mb-0">This Month</p>
          </b-card>
        </b-col>
        <b-col md="3">
          <b-card bg-variant="dark" text-variant="white">
            <h3 class="mb-0">PKR {{ formatMoney(stats.platformFees) }}</h3>
            <p class="mb-0">Platform Fees (5%)</p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Payments Table -->
      <b-table
        :items="filteredPayments"
        :fields="fields"
        :busy="loading"
        :per-page="perPage"
        :current-page="currentPage"
        striped
        hover
        responsive
        show-empty
      >
        <template #table-busy>
          <div class="text-center my-5">
            <b-spinner class="align-middle mr-2"></b-spinner>
            <strong>Loading payments...</strong>
          </div>
        </template>

        <template #cell(payment_id)="data">
          <code>{{ data.value }}</code>
        </template>

        <template #cell(student)="data">
          <div>
            <div class="font-weight-bold">{{ data.item.student_name || 'N/A' }}</div>
            <small class="text-muted">ID: {{ data.item.user_id }}</small>
          </div>
        </template>

        <template #cell(course)="data">
          <div>
            <div class="font-weight-bold">{{ data.item.course_title || 'N/A' }}</div>
            <small class="text-muted">ID: {{ data.item.course_id }}</small>
          </div>
        </template>

        <template #cell(amount)="data">
          <strong class="text-success">PKR {{ formatMoney(data.value) }}</strong>
        </template>

        <template #cell(platform_fee)="data">
          PKR {{ formatMoney(data.value) }}
        </template>

        <template #cell(teacher_credit)="data">
          PKR {{ formatMoney(data.value) }}
        </template>

        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant(data.value)">
            {{ data.value === 1 ? 'Completed' : 'Pending' }}
          </b-badge>
          <b-badge v-if="isIbft(data.item)" :variant="ibftVariant(data.item)" class="ml-1">{{ ibftLabel(data.item) }}</b-badge>
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.value) }}
        </template>

        <template #cell(actions)="data">
          <b-button variant="info" size="sm" @click="viewPayment(data.item)">
            <feather type="eye" class="feather-sm"></feather>
            View
          </b-button>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-row>
        <b-col md="6">
          <span class="text-muted">
            Showing {{ (currentPage - 1) * perPage + 1 }} to
            {{ Math.min(currentPage * perPage, filteredPayments.length) }} of
            {{ filteredPayments.length }} payments
          </span>
        </b-col>
        <b-col md="6">
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredPayments.length"
            :per-page="perPage"
            align="right"
            size="sm"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-card>

    <!-- Payment Detail Modal -->
    <b-modal
      v-model="showDetailModal"
      title="Payment Details"
      size="lg"
      ok-only
      ok-variant="secondary"
      ok-title="Close"
    >
      <template #modal-footer="{ ok }">
        <template v-if="selectedPayment && selectedPayment.method === 'ibft_pending'">
          <b-button variant="danger" :disabled="deciding" @click="decideIbft('reject')">Reject &amp; remove access</b-button>
          <b-button variant="success" :disabled="deciding" @click="decideIbft('confirm')">Confirm payment</b-button>
        </template>
        <b-button variant="secondary" :disabled="deciding" @click="ok()">Close</b-button>
      </template>
      <div v-if="selectedPayment">
        <b-row>
          <b-col md="6">
            <h6 class="text-muted">Payment ID</h6>
            <p><code>{{ selectedPayment.payment_id }}</code></p>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Status</h6>
            <p>
              <b-badge :variant="getStatusVariant(selectedPayment.status)">
                {{ selectedPayment.status === 1 ? 'Completed' : 'Pending' }}
              </b-badge>
            </p>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <h6 class="text-muted">Student</h6>
            <p>{{ selectedPayment.student_name || 'N/A' }}</p>
            <small class="text-muted">ID: {{ selectedPayment.user_id }}</small>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Course</h6>
            <p>{{ selectedPayment.course_title || 'N/A' }}</p>
            <small class="text-muted">ID: {{ selectedPayment.course_id }}</small>
          </b-col>
        </b-row>

        <b-row v-if="isIbft(selectedPayment)">
          <b-col md="6">
            <h6 class="text-muted">Method</h6>
            <p>{{ ibftLabel(selectedPayment) }}<span v-if="selectedPayment.method === 'ibft_pending'"> — student has temporary access</span></p>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Receipt</h6>
            <a v-if="selectedPayment.receipt_url" :href="selectedPayment.receipt_url" target="_blank" rel="noopener">
              <b-img :src="selectedPayment.receipt_url" thumbnail fluid style="max-height: 160px" alt="Payment receipt"></b-img>
              <div><small>Open full size</small></div>
            </a>
            <p v-else class="text-warning mb-0">No receipt uploaded</p>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <h6 class="text-muted">Amount Paid (Gross)</h6>
            <p class="text-success font-weight-bold">
              PKR {{ formatMoney(selectedPayment.amount) }}
            </p>
          </b-col>
          <b-col md="6">
            <h6 class="text-muted">Payment Date</h6>
            <p>{{ formatDate(selectedPayment.created_at) }}</p>
          </b-col>
        </b-row>

        <hr />

        <h6 class="text-muted mb-2">Fee Breakdown (5% platform fee)</h6>
        <b-row>
          <b-col md="4">
            <small class="text-muted">Course Fee</small>
            <p class="mb-0">PKR {{ formatMoney(selectedPayment.amount - (selectedPayment.platform_fee || 0)) }}</p>
          </b-col>
          <b-col md="4">
            <small class="text-muted">Platform Fee</small>
            <p class="mb-0">PKR {{ formatMoney(selectedPayment.platform_fee) }}</p>
          </b-col>
          <b-col md="4">
            <small class="text-muted">Teacher Credit</small>
            <p class="mb-0 font-weight-bold">PKR {{ formatMoney(selectedPayment.teacher_credit) }}</p>
          </b-col>
        </b-row>

        <b-row v-if="selectedPayment.transaction_id">
          <b-col>
            <h6 class="text-muted">Transaction ID</h6>
            <p><code>{{ selectedPayment.transaction_id }}</code></p>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import PaymentService from '@/services/payment.service';

export default {
  name: 'PaymentManagement',
  data() {
    return {
      payments: [],
      filteredPayments: [],
      loading: false,
      filter: {
        status: null,
        search: '',
      },
      stats: {
        totalRevenue: 0,
        totalPayments: 0,
        pendingPayments: 0,
        monthlyRevenue: 0,
        platformFees: 0,
      },
      fields: [
        { key: 'payment_id', label: 'Payment ID', sortable: true },
        { key: 'student', label: 'Student', sortable: true },
        { key: 'course', label: 'Course', sortable: true },
        { key: 'amount', label: 'Amount (Gross)', sortable: true },
        { key: 'platform_fee', label: 'Platform Fee (5%)', sortable: true },
        { key: 'teacher_credit', label: 'Teacher Credit', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'created_at', label: 'Date', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      deciding: false,
      perPage: 20,
      currentPage: 1,
      showDetailModal: false,
      selectedPayment: null,
    };
  },
  mounted() {
    this.loadPayments();
  },
  methods: {
    async loadPayments() {
      this.loading = true;
      try {
        const response = await PaymentService.getAllPayments();
        this.payments = response.data;
        this.filteredPayments = this.payments;
        this.calculateStats();
      } catch (error) {
        this.$bvToast.toast('Failed to load payments', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.loading = false;
      }
    },
    calculateStats() {
      // status is numeric (1 = paid) -- it was compared against the string
      // 'completed' here, which never matched, so these stats always showed 0.
      const paid = this.payments.filter(p => p.status === 1);
      this.stats.totalPayments = this.payments.length;
      this.stats.totalRevenue = paid.reduce((sum, p) => sum + Number(p.amount || 0), 0);
      this.stats.platformFees = paid.reduce((sum, p) => sum + Number(p.platform_fee || 0), 0);
      this.stats.pendingPayments = this.payments.filter(p => p.status !== 1).length;

      // Calculate this month's revenue
      const now = new Date();
      const thisMonth = paid.filter(p => {
        const paymentDate = new Date(p.created_at);
        return paymentDate.getMonth() === now.getMonth() &&
               paymentDate.getFullYear() === now.getFullYear();
      });
      this.stats.monthlyRevenue = thisMonth.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    },
    filterPayments() {
      this.filteredPayments = this.payments.filter(payment => {
        let matchesStatus = true;
        if (this.filter.status === 'completed') matchesStatus = payment.status === 1;
        else if (this.filter.status === 'pending' || this.filter.status === 'failed') matchesStatus = payment.status !== 1;
        const matchesSearch = !this.filter.search ||
          (payment.student_name && payment.student_name.toLowerCase().includes(this.filter.search.toLowerCase())) ||
          (payment.course_title && payment.course_title.toLowerCase().includes(this.filter.search.toLowerCase()));
        return matchesStatus && matchesSearch;
      });
      this.currentPage = 1;
    },
    searchPayments() {
      this.filterPayments();
    },
    refreshPayments() {
      this.loadPayments();
    },
    viewPayment(payment) {
      this.selectedPayment = payment;
      this.showDetailModal = true;
    },
    ibftLabel(payment) {
      if (payment.method === 'ibft_pending') return 'Bank transfer · awaiting confirmation';
      if (payment.method === 'ibft_rejected') return 'Bank transfer · rejected';
      return 'Bank transfer';
    },
    ibftVariant(payment) {
      if (payment.method === 'ibft_pending') return 'warning';
      if (payment.method === 'ibft_rejected') return 'danger';
      return 'info';
    },
    async decideIbft(decision) {
      const p = this.selectedPayment;
      if (!p || this.deciding) return;
      const confirm = decision === 'confirm';
      const ok = await this.$bvModal.msgBoxConfirm(
        confirm
          ? `Confirm that PKR ${this.formatMoney(p.amount)} from ${p.student_name || 'this student'} was received? The teacher will be credited.`
          : `Reject this payment? ${p.student_name || 'The student'} will lose access to "${p.course_title || 'the course'}".`,
        { title: confirm ? 'Confirm payment' : 'Reject payment', okVariant: confirm ? 'success' : 'danger', okTitle: confirm ? 'Confirm' : 'Reject', centered: true }
      );
      if (!ok) return;
      this.deciding = true;
      try {
        if (confirm) await PaymentService.confirmIbft(p.payment_id);
        else await PaymentService.rejectIbft(p.payment_id);
        this.$bvToast.toast(confirm ? 'Payment confirmed' : 'Payment rejected and access removed', { title: 'Success', variant: 'success', solid: true });
        this.showDetailModal = false;
        this.loadPayments();
      } catch (error) {
        this.$bvToast.toast(error.response?.data?.message || 'Failed to update payment', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.deciding = false;
      }
    },
    isIbft(payment) {
      return !!payment && String(payment.method || '').startsWith('ibft');
    },
    getStatusVariant(status) {
      // status is numeric: 1 = paid/completed.
      return status === 1 ? 'success' : 'warning';
    },
    formatMoney(amount) {
      return Number(amount || 0).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>
